import React, { useEffect, useState, useRef } from 'react';
import { themeAppearanceMode } from '../../../variables/theme-mode';
import { changeInitTheme } from '../../../../state/actions';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { withCurrentPage } from '../../../../shared-components/hoc-components/with-current-page';
import { compose } from 'redux';
import './MainLayout.sass';
import Footer from '../../Footer/Footer';
import CashierHeader from '@pano-project/components/CashierHeader/CashierHeader';
import { HeaderProvider } from '@pano-project/components/CashierHeader/HeaderContext';
import LayoutShell from './LayoutShell';
import { getLayoutClassNames } from './layout-class-names';
import { resolveHeaderButtons } from '@pano-project/variables/header-buttons';
import usePostResizeEvent from '../../../../utils/hooks/usePostResizeEvent';
import useGetLabelVariant from '@pano-project/hooks/useGetLabelVariant.ts';
import { ToastProvider } from '@pano-project/contexts/ToastContext';
import { isRecoveryResult } from '@pano-project/utils/isRecoveryResult';

const MainLayout = ({ children, currentPage: routeCurrentPage, page }) => {
  const currentPage = page || routeCurrentPage;
  const dispatch = useDispatch();
  const { getInputLabelVariant } = useGetLabelVariant();
  const store = useSelector(
    ({
      initTheme: {
        cashier_appearance_mode,
        cashier_theme,
        theme_editor_settings: {
          customization_settings: {
            appearance,
            layout,
            amount_field_type,
            show_balance,
            preselect_first_payment_method,
            preselect_only_available_method,
            header_style,
            show_back_button,
            show_close_button,
          } = {},
        } = {},
      },
      resultOfPay,
      profileInfo: { transaction_type: type, session_type } = {},
      gatewaysList: { gateways } = {},
    }) => ({
      cashier_appearance_mode,
      transaction_type: resultOfPay?.transaction_type,
      type,
      session_type,
      appearance,
      cashier_theme,
      layout,
      amount_field_type,
      show_balance,
      preselect_first_payment_method,
      preselect_only_available_method,
      header_style,
      show_back_button,
      show_close_button,
      gateways,
      resultOfPay,
    }),
    shallowEqual,
  );
  const {
    cashier_appearance_mode,
    transaction_type,
    type,
    session_type,
    appearance,
    cashier_theme,
    layout,
    amount_field_type,
    show_balance,
    preselect_first_payment_method,
    preselect_only_available_method,
    header_style,
    show_back_button,
    show_close_button,
    gateways,
    resultOfPay,
  } = store;

  const { showAnyHeaderButton } = resolveHeaderButtons({
    showBackButton: show_back_button,
    showCloseButton: show_close_button,
    layout,
    headerStyle: header_style,
  });

  const [cashierMode, setCashierMode] = useState(cashier_appearance_mode ?? '');
  const mainPages = ['gatewaysPage', 'amountPage', 'paymentPage', 'redirectEmbeddedPage'];
  const buttonsOnlyPages = ['resultPage', 'errorPage', 'redirectPage'];
  const isAmountFirst = layout === 'amount-first';
  const isPanoramicLayout = layout === 'panoramic';
  const showHeader = mainPages.includes(currentPage);
  const showFooter = !['redirectPage', 'errorPage'].includes(currentPage);
  const input_field_style = getInputLabelVariant();
  const isPreselectedPM =
    isAmountFirst ||
    !!preselect_first_payment_method ||
    (gateways?.length === 1 && !!preselect_only_available_method);

  const pageRendersOwnHeader =
    isAmountFirst && currentPage === 'resultPage' && isRecoveryResult(resultOfPay, isAmountFirst);

  const inputFieldStyleClass =
    isPanoramicLayout && input_field_style
      ? `input-field-style--${input_field_style.replaceAll('_', '-')}`
      : isAmountFirst &&
          (input_field_style === 'floating_label' || input_field_style === 'no_label')
        ? `input-field-style--${input_field_style.replaceAll('_', '-')}`
        : '';

  const wrapRef = useRef(null);
  const containerRef = useRef(null);
  usePostResizeEvent({ wrapRef, containerRef });

  useEffect(() => {
    const cashier_mode = themeAppearanceMode(appearance, cashier_theme);
    setCashierMode(cashier_mode);
    dispatch(changeInitTheme({ cashier_appearance_mode: cashier_mode }));
  }, [appearance, cashier_theme]);

  return (
    <ToastProvider>
      <HeaderProvider>
        <LayoutShell
          transactionType={type ? type : transaction_type}
          className={getLayoutClassNames({
            layout,
            headerStyle: header_style,
            appearanceMode: cashierMode,
            currentPage,
            sessionType: session_type,
            amountFieldType: amount_field_type,
            showBalance: show_balance,
            showHeaderButtons: showAnyHeaderButton,
            isPreselectedPM,
            inputFieldStyleClass,
          })}
          wrapRef={wrapRef}
          containerRef={containerRef}
        >
          {((!isAmountFirst ? showHeader : currentPage === 'redirectEmbeddedPage') ||
            (showAnyHeaderButton &&
              buttonsOnlyPages.includes(currentPage) &&
              !pageRendersOwnHeader)) && <CashierHeader />}
          {children}
          {showFooter && !isAmountFirst && <Footer />}
        </LayoutShell>
      </HeaderProvider>
    </ToastProvider>
  );
};

export default compose(withCurrentPage)(MainLayout);
